import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert, CircularProgress } from '@mui/material';
import { Box } from '@mui/system';

import ModalNotification from '@/shared/components/dialogs/ModalNotification';
import WaveUIForm_V2 from '@/shared/components/forms/WaveUIForm_V2';
import FieldType from '@/shared/components/forms/WaveUIForm_V2/types/fieldType.enum';
import BookingsService from '@/shared/types/bookings/service.type';

type BookingServiceEditDialogProps = {
  bookingService: BookingsService;
  loading: boolean;
  errors?: string[];
  onClose: () => void;
  proceed: (formValues: Partial<BookingsService>) => void;
};

const BookingServiceEditDialog: FC<BookingServiceEditDialogProps> = ({
  bookingService,
  loading,
  errors,
  onClose,
  proceed,
}) => {
  const { t } = useTranslation(['bookingsServices', 'forms', 'common']);

  const [bookingServiceForm, setBookingServiceForm] = useState<
    Partial<BookingsService>
  >({
    booking_service_name: bookingService.booking_service_name,
  });

  const [bookingServiceErrors, setBookingServiceErrors] = useState<
    Partial<BookingsService>
  >({});

  const onValidate = () => {
    const errors: Partial<BookingsService> = {};

    if (!bookingServiceForm.booking_service_name) {
      errors['booking_service_name'] = t('forms:booking_service_name_required');
    }

    setBookingServiceErrors(errors);
    return !Object.keys(errors).length;
  };

  if (errors && errors.length > 0) {
    return (
      <ModalNotification
        size='xs'
        fullWidth
        open
        onClose={onClose}
        dialogProps={{
          title: t('bookingsServices:editBookingServiceName'),
          content: errors.map((error) => (
            <Alert key={error} severity='error'>
              {error}
            </Alert>
          )),
          actions: [
            {
              label: t('common:close'),
              onClick: onClose,
            },
          ],
        }}
      />
    );
  }

  return (
    <ModalNotification
      size='xs'
      fullWidth
      open
      onClose={onClose}
      dialogProps={{
        title: t('bookingsServices:editBookingServiceName'),
        content: (
          <Box sx={{ py: 1 }}>
            <WaveUIForm_V2
              fields={[
                {
                  field_type: FieldType.Text,
                  field_props: {
                    label: t('forms:booking_service_name'),
                    name: 'booking_service_name',
                    required: true,
                    placeholder: t('forms:booking_service_name_placeholder'),
                    value: bookingServiceForm.booking_service_name,
                    error: !!bookingServiceErrors.booking_service_name,
                    helperText: bookingServiceErrors.booking_service_name,
                  },
                },
              ]}
              getFieldsValues={(value) => {
                setBookingServiceForm((prev) => ({ ...prev, ...value }));
              }}
            />
          </Box>
        ),
        actions: [
          {
            label: t('common:cancel'),
            onClick: onClose,
          },
          {
            label: t('common:update'),
            variant: 'contained',
            icon: loading ? (
              <CircularProgress size='16px' color='inherit' sx={{ mr: 1 }} />
            ) : null,
            onClick: () => {
              const isFormValid = onValidate();

              if (isFormValid && !loading) {
                proceed(bookingServiceForm);
              }
            },
          },
        ],
      }}
    />
  );
};

export default BookingServiceEditDialog;
